<template>
  <div>
    <div v-if="$apollo.loading">
      <loading-spinner class="my-5 text-center"/>
      <div class="text-center">Your Teams channels are being retrieved, this can take a few seconds.</div>
    </div>

    <template v-else>

      <div class="fixed-main-box">
        <!-- Schedule Form -->
        <b-row v-if="!viewOnly" class="mb-3">
          <b-col cols="12" class="pb-2">
            <h4><strong>Schedule Message</strong></h4>
          </b-col>
          <b-col v-if="!sendAtText.error" cols="12" class="d-flex justify-content-start align-items-center">
            <div>
              This message will be sent at
            </div>
            <div style="width: 100px;" class="ml-2">
              <b-form-select v-model="relativeSendAtForm.atHour" :options="hourOptions" />
            </div>
            <div class="ml-2">:</div>
            <div style="width: 100px;" class="ml-2">
              <b-form-select v-model="relativeSendAtForm.atMinute" :options="minuteOptions" />
            </div>
            <div class="ml-2" v-html="sendAtText.text">
            </div>
          </b-col>
          <b-col v-else cols="12">
            <div v-html="sendAtText.text"></div>
          </b-col>
        </b-row>

        <!-- Main Form -->
        <b-row class="mt-4">
          <b-col cols="12">
          <h4><strong>Message Delivery</strong></h4>
        </b-col>
        <b-col v-if="isScheduled && !viewOnly" cols="12">
          <b-alert show>You cannot edit this message, as it is either delivered or in progress.</b-alert>
        </b-col>
          <b-col cols="12">
            <advanced-form
              autocomplete="off"
              :hideFormControl="true"
              :form="formFields"
              :disabled="viewOnly"
              v-bind="form"
              v-model="form"
            >

              <b-alert
                v-if="(!Array.isArray(syncedEmailAddresses) || !syncedEmailAddresses.length) && !viewOnly"
                slot="from-before"
                variant="warning"
                show
              >
                It seems like no accounts have been connected to Real Links yet. In order for us to send Microsoft Teams messages on your behalf, you need to go to <a :href="$router.resolve({ name: 'EmailSetup' }).href" target="_blank">Settings > Connect Teams</a> page.
              </b-alert>
            </advanced-form>
          </b-col>
        </b-row>

        <!-- Input Form with preview -->
        <b-row class="mb-3">
          <b-col cols="12">
            <h4 class="pt-3 pb-2"><strong>Message Content</strong></h4>
          </b-col>
          <b-col cols="12" lg="7">
            <collective-goal-preview
              v-if="templateID === 'teams_collective_goal'"
              class="mb-3"
              :fromEmail="form.from"
              :job="inputForm.job || null"
              :contentShare="inputForm.contentShare || null"
              :collectiveGoal="collectiveGoal"
              :inputs="inputForm"
              @sendTestMessage="$bvModal.show('send-test-message-modal')"
            />
            <promote-content-preview
              v-else-if="['teams_promote_a_job', 'teams_promote_an_internal_job', 'teams_promote_content'].includes(templateID)"
              class="mb-3"
              :fromEmail="form.from"
              :job="inputForm.job || null"
              :contentShare="inputForm.contentShare || null"
              :collectiveGoal="collectiveGoal"
              :inputs="inputForm"
              :isInternal="templateID === 'teams_promote_an_internal_job'"
              @sendTestMessage="$bvModal.show('send-test-message-modal')"
            />
            <promote-multiple-content-preview
              v-else-if="['teams_promote_jobs', 'teams_promote_internal_jobs', 'teams_promote_content_shares', 'teams_promote_content_shares_without_back_to_platform', 'teams_promote_content_shares_read_only'].includes(templateID)"
              class="mb-3"
              :fromEmail="form.from"
              :jobs="inputForm.jobs || null"
              :contentShares="inputForm.contentShares || null"
              :inputs="inputForm"
              :isInternal="templateID === 'teams_promote_internal_jobs'"
              @sendTestMessage="$bvModal.show('send-test-message-modal')"
            />
            <schedule-multiple-content-preview
              v-else-if="templateID === 'teams_schedule_jobs' || templateID === 'teams_schedule_content_shares'"
              class="mb-3"
              :fromEmail="form.from"
              :jobs="inputForm.jobs || null"
              :contentShares="inputForm.contentShares || null"
              :inputs="inputForm"
              @sendTestMessage="$bvModal.show('send-test-message-modal')"
            />
            <leaderboard-preview
              v-else-if="templateID === 'teams_leaderboard_individual' || templateID === 'teams_leaderboard_overall'"
              class="mb-3"
              :fromEmail="form.from"
              :job="inputForm.job || null"
              :contentShare="inputForm.contentShare || null"
              :isIndividual="templateID === 'teams_leaderboard_individual'"
              :inputs="inputForm"
              @sendTestMessage="$bvModal.show('send-test-message-modal')"
            />
            <gamification-preview
              v-else-if="templateID === 'teams_gamification_individual'"
              class="mb-3"
              :fromEmail="form.from"
              :collectiveGoal="collectiveGoal"
              :job="inputForm.job || null"
              :contentShare="inputForm.contentShare || null"
              :inputs="inputForm"
              @sendTestMessage="$bvModal.show('send-test-message-modal')"
            />
            <invite-preview
              v-else-if="templateID === 'teams_invite'"
              class="mb-3"
              :fromEmail="form.from"
              :collectiveGoal="collectiveGoal"
              :job="inputForm.job || null"
              :contentShare="inputForm.contentShare || null"
              :inputs="inputForm"
              @sendTestMessage="$bvModal.show('send-test-message-modal')"
            />
            <content-preview
              v-else
              class="mb-3"
              :fromEmail="form.from"
              :job="inputForm.job || null"
              :contentShare="inputForm.contentShare || null"
              :jobs="inputForm.jobs || null"
              :contentShares="inputForm.contentShares || null"
              :collectiveGoal="collectiveGoal"
              :inputs="inputForm"
              :body="body"
              @sendTestMessage="$bvModal.show('send-test-message-modal')"
            />
            <div class="mt-3 text-muted">Please note that the display below is just an estimated layout. Use the "Send Test" button to get an accurate preview.</div>
          </b-col>
          <b-col cols="12" lg="5">
            <advanced-form
              autocomplete="off"
              :hideFormControl="true"
              :form="inputFormFields"
              v-bind="inputForm"
              v-model="inputForm"
              :disabled="viewOnly"
            />
          </b-col>
        </b-row>

        <!-- Variables that can be used -->
        <b-row class="pb-3">
          <b-col>
            <b-card no-body class="mb-1">
              <b-card-header class="d-flex justify-content-between align-items-center bg-white">
                <div @click="displayVariables = !displayVariables" v-b-toggle.available-variables-card>
                  <strong>Template variables you can use</strong>
                </div>
                <b-button @click="displayVariables = !displayVariables"  v-b-toggle.available-variables-card variant="outline-primary" class="px-2" size="sm">
                  <font-awesome-icon v-if="displayVariables" icon="chevron-up" size="sm"/>
                  <font-awesome-icon v-else icon="chevron-down" size="sm"/>
                </b-button>
              </b-card-header>
              <b-collapse id="available-variables-card" accordion="available-variables-card" role="tabpanel" class="p-3">
                <b>User related variables</b> you can use:<br>
                <span v-for="{ key, description } in variables.filter(({ type }) => type === 'USER')" :key="key" v-html="`{{${key}}} - ${description}<br>`"></span><br>
                <b>Job related variables</b> you can use:<br>
                <span v-for="{ key, description } in variables.filter(({ type }) => type === 'JOB')" :key="key" v-html="`{{${key}}} - ${description}<br>`"></span><br>
                <b>Leaderboard related variables</b> you can use:<br>
                <span v-for="{ key, description } in variables.filter(({ type }) => type === 'LEADERBOARD')" :key="key" v-html="`{{${key}}} - ${description}<br>`"></span><br>
              </b-collapse>
            </b-card>
          </b-col>
        </b-row>
      </div>

      <!-- Buttons -->
      <div v-if="!viewOnly" class="fixed-buttons-box border-top d-flex justify-content-between align-items-center">
        <!-- Delete Button -->
        <b-link
          v-if="!isScheduled"
          class="text-danger"
          @click="$bvModal.show('delete-teams-message')"
        >Delete</b-link>

        <!-- Save Button -->
        <b-button
          variant="primary"
          @click="input('teamsMessage')"
          class="float-right"
          :disabled="isScheduled || isSceduledInPast"
          pill
        >
          Save Message
        </b-button>
      </div>

      <!-- Test Message Form -->
      <b-modal id="send-test-message-modal" title="Send Test Message" :hide-footer="true">
        <b-row>
          <b-col v-if="!viewOnly">
            <b-form>
              <!-- Email Addresses Field -->
              <test-message-form
                channel="teams"
                :body="body"
                :from="form.from"
                :job="inputForm.job"
                :channelID="form.channel"
                :contentShare="inputForm.contentShare"
                :jobs="inputForm.jobs"
                :contentShares="inputForm.contentShares"
                :collectiveGoal="collectiveGoal"
                :isIndividual="isIndividual"
                :campaignID="campaignID"
                :campaignComponentID="campaignComponentID"
                :preferences="campaign.preferences"
                :inputs="inputs"
                :template="template.id"
                :templateDefinition="JSON.stringify(template)"
              />
            </b-form>
          </b-col>
          <b-col v-else>
            <b-alert show>You cannot send a test message in view only mode.</b-alert>
          </b-col>
        </b-row>
      </b-modal>

      <!-- ALERT MODALS -->
      <b-modal id="target-group-change" title="Target group has changed" ok-only ok-title="I Understand">
        <div v-if="isIndividual">
          <p>This Microsoft Teams message will be sent as a direct message to everyone in the selected group.</p>
        </div>
        <div v-else>
          <p>This Microsoft Teams message will be sent as a group message to everyone in the selected group.</p>
        </div>
      </b-modal>

      <!-- DELETE MODAL -->
      <b-modal
        v-if="!isScheduled"
        id="delete-teams-message"
        title="Delete Teams Message"
        ok-title="Delete"
        cancel-title="Cancel"
        @ok="$emit('delete', { moveComponents })"
        cancel-variant="light"
        ok-variant="danger"
      >
        <p>Are you sure you want to delete this teams message?</p>
        <b-form-checkbox v-model="moveComponents" name="check-button" switch>
          Move all the following messages forward
        </b-form-checkbox>
      </b-modal>
    </template>
  </div>

</template>

<script>
import AdvancedForm from '@src/components/AdvancedForm';
import teamsTemplates from '@src/views/campaign-admin/templates/teams';
import LoadingSpinner from '@/components/misc/LoadingSpinner';
import Share from '@src/components/Share';
import gridMixin from '@src/mixins/grid';
import ContentPreview from '@src/components/campaign-admin/message-previews/teams/content';
import CollectiveGoalPreview from '@src/components/campaign-admin/message-previews/teams/collectiveGoal';
import PromoteContentPreview from '@src/components/campaign-admin/message-previews/teams/promoteContent';
import InvitePreview from '@src/components/campaign-admin/message-previews/teams/invite';
import PromoteMultipleContentPreview from '@src/components/campaign-admin/message-previews/teams/promoteMultipleContent';
import ScheduleMultipleContentPreview from '@src/components/campaign-admin/message-previews/teams/scheduleMultipleContent';
import LeaderboardPreview from '@src/components/campaign-admin/message-previews/teams/leaderboard';
import GamificationPreview from '@src/components/campaign-admin/message-previews/teams/gamification';
import TestMessageForm from '@src/components/campaign-admin/test-message-form.vue';
import { mapRoot, mapPreferences } from '@src/mixins/root';
import updateMessageMixin from '@/mixins/updateMessage';
import gql from 'graphql-tag';

const TeamsChannelsQuery = gql`
  query TeamsChannelsQuery {
    user {
      id
      teamsChannels {
        groups {
          id
          title
        }
        teams {
          id
          title
          channels {
            id
            title
          }
        }
      }
    }
  }
`;

export default {
  components: {
    AdvancedForm,
    Share,
    TestMessageForm,
    ContentPreview,
    CollectiveGoalPreview,
    PromoteContentPreview,
    PromoteMultipleContentPreview,
    ScheduleMultipleContentPreview,
    InvitePreview,
    LeaderboardPreview,
    GamificationPreview,
    LoadingSpinner
  },
  apollo: {
    channels: {
      query: TeamsChannelsQuery,
      update(data) {
        const { user } = data || {};
        const { teamsChannels } = user || {};
        // const { teams, groups } = teamsChannels || {};
        const { teams } = teamsChannels || {};
        const channels = {
          // ...(Array.isArray(groups) ? groups.reduce((r, group) => ({ ...r, [group.id]: group.title }), {}) : {}),
          ...(Array.isArray(teams) ? teams.reduce((r, team) => {
            const { channels, title, id: teamID } = team || {};
            return {
              ...r,
              ...(Array.isArray(channels) ? channels.reduce((result, channel) => ({ ...result, [`${teamID}||${channel.id}`]: `${title} - ${channel.title}` }), {}) : {})
            };
          }, {}) : {})
        };
        console.log({ channels, data });
        return channels;
      },
      skip() {
        const { form } = this;
        if (form && form.to === 'CHANNEL') {
          console.log('START QUERY');
          return false;
        }
        return true;
      },
      error(err) {
        this.$notifyFatalErr(err, { prepend: 'Failed to load Teams channels' });
      },
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
    },
  },
  data() {
    const { teamsMessage } = this.value || {};
    delete teamsMessage.__typename;
    const { template, inputs, body, job, contentShare, jobs, contentShares, relativeSendAt, ...rest } = teamsMessage || {};
    return {
      moveComponents: false,
      form: {
        from: null,
        to: 'ALL_USERS',
        channel: null,
        sendAt: '',
        status: 'DRAFT',
        ...(rest || {})
      },
      relativeSendAt: {
        ...(relativeSendAt || {})
      },
      relativeSendAtForm: {
        atHour: relativeSendAt.atHour || 12,
        atMinute: relativeSendAt.atMinute || 0,
      },
      templateID: template || null,
      isScheduled: false,
      inputForm: {
        ...Object.keys(inputs || {}).reduce((acc, key) => {
          if (key.includes('IMAGE')) {
            try {
              acc[key] = JSON.parse(inputs[key]);
            } catch (e) {
              console.log(e);
              acc[key] = inputs[key];
            }
          } else {
            acc[key] = inputs[key];
          }
          return acc;
        }, {}),
        ...(job ? { job } : {}),
        ...(contentShare ? { contentShare } : {}),
        ...(jobs ? { jobs } : {}),
        ...(contentShares ? { contentShares } : {})
      },
      displayVariables: false,
    };
  },
  mixins: [
    gridMixin,
    updateMessageMixin
  ],
  computed: {
    ...mapPreferences([ 'featuresEnabled' ]),
    ...mapRoot([ 'isLNW', 'isApplusIdiada' ]),
    template() {
      const { templateID, isLNW, isApplusIdiada } = this;
      console.log({ templateID, isLNW, isApplusIdiada });
      let templateFolder = null;
      if (isLNW) {
        templateFolder = 'lnw';
      }
      if (isApplusIdiada) {
        templateFolder = 'applus';
      }
      return teamsTemplates(templateFolder).find(({ id }) => id === templateID);
    },
    toChoices() {
      const { isIndividual, template } = this;
      const { canSendToChannel } = template;
      return {
        ALL_USERS: `All Participants: Everyone in the campaign - ${isIndividual ? 'sent as a direct Teams message' : 'posted in a group'}`,
        REGISTERED_USERS: `Active Participants: Those who have engaged with the campaign - ${isIndividual ? 'sent as a direct Teams message' : 'posted in a group'}`,
        INVITED_USERS: `Inactive Participants: Those who have not engaged with the campaign yet - ${isIndividual ? 'sent as a direct Teams message' : 'posted in a group'}`,
        ...(canSendToChannel ? { CHANNEL: 'Channel: Sent as a channel message' } : {}),
      };
    },
    formFields() {
      const { syncedEmailAddresses, campaignID } = this;
      console.log({ campaignID });
      const defaultEmailOption = { NONE: '---- Select an email address ----' };
      return {
        from: {
          type: 'SELECT',
          label: 'From',
          disabled: !Array.isArray(syncedEmailAddresses) || !syncedEmailAddresses.length || this.isScheduled,
          choices: Array.isArray(syncedEmailAddresses) && syncedEmailAddresses.length
            ? syncedEmailAddresses.reduce((out, { emailAddress, fullName }) => {
              out[emailAddress] = `${emailAddress} (${fullName})`;
              return out;
            }, defaultEmailOption) : [ defaultEmailOption ],
          helpText: 'The person on whose behalf this message will be delivered i.e. the sender of the message'
        },
        to: {
          label: 'To',
          type: 'SELECT',
          required: true,
          choices: this.toChoices,
          disabled: this.isScheduled,
          helpText: 'The target group of this Microsoft Teams message'
        },
        ...(this.form.to === 'CHANNEL' && this.channels ? {
          channel: {
            label: 'Channel',
            type: 'SELECT',
            required: true,
            disabled: this.isScheduled,
            choices: this.channels,
            helpText: 'The target group of this Microsoft Teams message'
          },
        } : {}),
        status: {
          label: 'Status',
          type: 'SELECT',
          required: true,
          disabled: this.isScheduled,
          choices: {
            READY: 'Ready',
            DRAFT: 'Draft',
          },
        },
      };
    },
    inputFormFields() {
      const { template, jobsWhere, contentSharesWhere } = this;
      console.log({ template });
      const inputFields = template ? template.inputs : {};
      return {
        ...(this.isJob ? {
          job: {
            label: 'Job',
            type: 'RELATION',
            required: true,
            options: {
              type: 'jobs',
              title: 'Jobs',
              where: jobsWhere || {
                status: 'ACTIVE',
              },
              many: false,
              order: 'CREATED_DESC',
            },
            helpText: 'Select the job you want to include in the Microsoft Teams message',
          },
        } : {}),
        ...(this.areJobs ? {
          jobs: {
            label: 'Jobs',
            type: 'RELATION',
            required: true,
            options: {
              type: 'jobs',
              title: 'Jobs',
              where: jobsWhere || {
                status: 'ACTIVE',
              },
              many: true,
              order: 'CREATED_DESC',
            },
            helpText: 'Select up to 4 jobs you want to include in the Microsoft Teams message',
          },
        } : {}),
        ...(this.isContent ? {
          contentShare: {
            label: 'Content',
            type: 'RELATION',
            required: true,
            options: {
              type: 'contentshares',
              title: 'Content',
              many: false,
              where: contentSharesWhere || {
                status: 'ACTIVE',
              },
              order: 'CREATED_DESC',
            },
            helpText: 'Select the piece of content you want to include in the Microsoft Teams message',
          },
        } : {}),
        ...(this.arePiecesOfContent ? {
          contentShares: {
            label: 'Content',
            type: 'RELATION',
            required: true,
            options: {
              type: 'contentshares',
              title: 'Content',
              many: true,
              where: contentSharesWhere || {
                status: 'ACTIVE',
              },
              order: 'CREATED_DESC',
            },
            helpText: 'Select up to 4 pieces of content you want to include in the Microsoft Teams message',
          },
        } : {}),
        ...inputFields,
      };
    },
    hasContentSharesV2Enabled() {
      return (this.featuresEnabled || []).includes('content-share-v2');
    },
    body() {
      const { template, inputForm, hasContentSharesV2Enabled } = this;
      if (!template) return null;
      const { body: rawBody } = template;
      const body = typeof rawBody === 'object' ? JSON.parse(JSON.stringify(rawBody)) : rawBody;
      if (body && typeof body === 'string') {
        let replacedBody = body;
        Object.keys(inputForm).forEach(key => {
          if (typeof inputForm[key] === 'string') {
            replacedBody = replacedBody.replaceAll(`[[${key}]]`, inputForm[key]);
          }
        });
        return replacedBody;
      };
      const { jobs, contentShares } = inputForm;
      if (body && body.body && body.body.content && Array.isArray(body.attachments) && template.blocks && (Array.isArray(jobs) || Array.isArray(contentShares))) {
        // it only supports one type of blocks
        const [ blockKey ] = Object.keys(template.blocks);
        let items = [];
        if (blockKey === 'BLOCK_JOB') {
          const { jobs } = inputForm;
          items = jobs;
        }
        if (blockKey === 'BLOCK_CONTENT') {
          const { contentShares } = inputForm;
          items = contentShares;
        }
        console.log({ items });
        // create attachments
        const attachments = items.map(({ id, customFields }) => {
          const postType = (customFields && customFields.displayContent) || 'link';
          const contentType = hasContentSharesV2Enabled && [ 'image', 'video', 'document' ].includes(postType) ? 'linkedin-only' : 'default';
          return JSON.parse(JSON.stringify(template.attachment(id, contentType)).replaceAll('[ID]', id));
        });
        body.attachments = [
          ...body.attachments,
          ...attachments
        ];

        // update content
        const attachmentBlocks = items.map(({ id }) => template.blocks[blockKey](id)).join('');
        body.body.content = rawBody.body.content.replaceAll(`<${blockKey}>`, attachmentBlocks);
      }
      let replacedBody = JSON.stringify(body);
      Object.keys(inputForm).forEach(key => {
        // console.log({ key, value: inputForm[key], turnDown: turndownService.turndown(inputForm[key]).replaceAll('\\', '') });
        console.log({ key, value: inputForm[key] });
        if (typeof inputForm[key] === 'string') {
          replacedBody = replacedBody.replaceAll(`[[${key}]]`, inputForm[key].replace(/\s/g, ' '));
        }
      });
      console.log({ replacedBody: JSON.parse(replacedBody) });
      // console.log({ replacedBody });
      return replacedBody;
    },
    isIndividual() {
      // const keys = this.variables.filter(({ target }) => target === 'INDIVIDUAL').map(({ key }) => key);
      // const { relations } = this.template || {};
      // return this.templateID === 'teams_invite' || !!keys.filter(key => this.usedVariables.includes(key)).length || (Array.isArray(relations) && (relations.includes('MANY_JOBS') || relations.includes('MANY_CONTENT')));
      return true;
    },
  },
};
</script>

<style scoped>
.fixed-buttons-box {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: white;
  padding: 8px 10px 8px 10px;
  border-top: 1px solid #e9ecef;
  z-index: 1000;
}
.fixed-main-box {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 60px;
  padding: 20px;
  overflow-y: scroll;
}
</style>



// WEBPACK FOOTER //
// src/components/campaign-admin/update-teams-message-relative-dates.vue